import React, { useState, useEffect } from 'react';
import { router, Head } from '@inertiajs/react';
import { motion, AnimatePresence } from 'framer-motion';
import ParticleBackground from '../../components/ParticleBackground';
import Header from '../../components/Header';
import Footer from '../../components/Footer';

const VERDICT_STYLES = {
  YES: { label: 'YES', gradient: 'from-emerald-400 to-green-500', glow: 'rgba(16,185,129,0.7)', emoji: '✅' },
  NO: { label: 'NO', gradient: 'from-rose-400 to-red-500', glow: 'rgba(244,63,94,0.7)', emoji: '🚫' },
  MAYBE: { label: 'MAYBE', gradient: 'from-amber-400 to-yellow-500', glow: 'rgba(245,158,11,0.7)', emoji: '🤔' },
};

export default function Reveal({ reading, spread, cards, interpretation, verdict, meta_tags }) {
  const verdictStyle = verdict ? VERDICT_STYLES[String(verdict).toUpperCase()] : null;

  // Positions are plain strings (e.g. "Past"); tolerate the legacy object shape.
  const positionName = (index) => {
    const position = spread.positions?.[index];
    if (!position) return `Card ${index + 1}`;
    return typeof position === 'string' ? position : (position.name || `Card ${index + 1}`);
  };
  const [revealedCards, setRevealedCards] = useState([]);
  const [showInterpretation, setShowInterpretation] = useState(false);
  const [isPolling, setIsPolling] = useState(!interpretation);
  const [cardsMinimized, setCardsMinimized] = useState(false);
  const [email, setEmail] = useState('');
  const [emailGateUnlocked, setEmailGateUnlocked] = useState(() => !!localStorage.getItem(`tarot_unlocked_${reading.uuid}`));
  const [emailError, setEmailError] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);
  const [selectedCardModal, setSelectedCardModal] = useState(null);

  const handleCardReveal = (cardId) => {
    if (!revealedCards.includes(cardId)) {
      setRevealedCards([...revealedCards, cardId]);

      // Trigger animation sequence when all cards are revealed
      if (revealedCards.length + 1 === cards.length) {
        setTimeout(() => {
          setCardsMinimized(true);
          setTimeout(() => setShowInterpretation(true), 800);
        }, 1500);
      }
    }
  };

  const isCardRevealed = (cardId) => revealedCards.includes(cardId);
  const allCardsRevealed = revealedCards.length === cards.length;

  // Poll for interpretation if not ready
  useEffect(() => {
    if (!isPolling || interpretation) {
      setIsPolling(false);
      return;
    }

    const pollInterval = setInterval(() => {
      router.reload({ only: ['interpretation'] });
    }, 2000);

    return () => clearInterval(pollInterval);
  }, [isPolling, interpretation]);

  // Stop polling when interpretation arrives
  useEffect(() => {
    if (interpretation && isPolling) {
      setIsPolling(false);
      if (allCardsRevealed) {
        setShowInterpretation(true);
      }
    }
  }, [interpretation, isPolling, allCardsRevealed]);


  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setEmailError('');
    setEmailLoading(true);
    try {
      const response = await fetch('/email_subscriptions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email_subscription: {
            email: email,
            source: 'reading_reveal',
            reading_id: reading.id
          }
        })
      });

      if (response.ok) {
        localStorage.setItem(`tarot_unlocked_${reading.uuid}`, '1');
        setEmailGateUnlocked(true);
      } else {
        const data = await response.json();
        setEmailError(data.errors?.[0] || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      setEmailError('Something went wrong. Please try again.');
    } finally {
      setEmailLoading(false);
    }
  };

  return (
    <>
      <Head>
        <title>Your Tarot Reading | Tarot-Reader.AI</title>
        <meta name="description" content="Discover the insights from your tarot card reading" />
        {meta_tags?.canonical && <link rel="canonical" href={meta_tags.canonical} />}
      </Head>

      <div className="bg-gradient-to-b from-indigo-950 via-purple-900 to-indigo-950 min-h-screen relative overflow-hidden flex flex-col">
      <ParticleBackground />
      <Header />

      <div className="relative z-10 flex-1 py-8 pb-20 sm:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <motion.div
            className="text-center mb-8"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {/* Animated Icon */}
            <motion.div
              className="inline-block mb-6"
              animate={{
                rotate: [0, 5, -5, 0],
                scale: [1, 1.1, 1],
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
            >
              <div className="relative">
                <span className="text-7xl drop-shadow-[0_0_20px_rgba(251,191,36,0.5)]">🔮</span>
                {/* Glow effect */}
                <motion.div
                  className="absolute inset-0 blur-xl bg-amber-500/30 rounded-full"
                  animate={{
                    scale: [1, 1.2, 1],
                    opacity: [0.3, 0.6, 0.3],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                  }}
                />
              </div>
            </motion.div>

            <h1 className="text-4xl md:text-5xl font-heading font-bold mb-4">
              <span className="bg-gradient-to-r from-amber-200 via-purple-300 to-pink-300 bg-clip-text text-transparent drop-shadow-lg">
                Your Reading
              </span>
            </h1>
            <p className="text-xl text-purple-100 mb-2">{reading.question}</p>
            <p className="text-amber-200 font-semibold flex items-center justify-center gap-2">
              <span>🎴</span>
              {spread.name}
              <span>🎴</span>
            </p>
          </motion.div>

          {/* Progress Bar */}
          <motion.div
            className="max-w-md mx-auto mb-8"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3 }}
          >
            <div className="relative h-3 bg-purple-900/50 rounded-full overflow-hidden border border-purple-400/30">
              <motion.div
                className="absolute inset-y-0 left-0 bg-gradient-to-r from-amber-500 via-purple-500 to-pink-500 rounded-full"
                animate={{ width: `${(revealedCards.length / cards.length) * 100}%` }}
                transition={{ duration: 0.5, ease: 'easeOut' }}
              />
            </div>
            <p className="text-center mt-3 text-sm text-purple-200 font-semibold">
              ✨ {revealedCards.length} of {cards.length} cards revealed ✨
            </p>
          </motion.div>

          {/* Instructions */}
          <AnimatePresence>
            {!allCardsRevealed && (
              <motion.div
                className="text-center mb-6"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ delay: 0.5 }}
              >
                <p className="text-lg text-amber-300 font-semibold animate-pulse flex items-center justify-center gap-2">
                  <span>💫</span>
                  Click each card to reveal its wisdom
                  <span>💫</span>
                </p>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Cards Container */}
          <AnimatePresence mode="wait">
            {!cardsMinimized ? (
              /* Full Size Cards */
              <motion.div
                className="flex justify-center items-start gap-4 md:gap-6 flex-wrap mb-12 px-2"
                layout
              >
                {cards.map((card, index) => {
                  const isRevealed = isCardRevealed(card.id);

                  return (
                    <motion.div
                      key={card.id}
                      className="flex flex-col items-center"
                      initial={{ opacity: 0, y: 50 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.2, duration: 0.6 }}
                      layout
                    >
                      {/* Card */}
                      <motion.div
                        className={`relative ${!isRevealed ? 'cursor-pointer' : 'cursor-default'}`}
                        style={{
                          width: 'clamp(120px, 25vw, 160px)',
                          height: 'clamp(180px, 37.5vw, 240px)',
                          perspective: '1000px'
                        }}
                        onClick={() => !isRevealed && handleCardReveal(card.id)}
                        whileHover={!isRevealed ? {
                          scale: 1.05,
                          rotateZ: [-2, 2, -2],
                          transition: { rotateZ: { repeat: Infinity, duration: 0.5 } }
                        } : {}}
                        whileTap={!isRevealed ? { scale: 0.95 } : {}}
                      >
                        {/* Glow effect on hover */}
                        {!isRevealed && (
                          <motion.div
                            className="absolute inset-0 rounded-lg"
                            style={{
                              background: 'radial-gradient(circle at center, rgba(251, 191, 36, 0.4) 0%, transparent 70%)',
                              filter: 'blur(20px)',
                            }}
                            animate={{
                              opacity: [0.5, 1, 0.5],
                            }}
                            transition={{
                              duration: 2,
                              repeat: Infinity,
                              ease: 'easeInOut',
                            }}
                          />
                        )}

                        <motion.div
                          className="relative w-full h-full"
                          style={{ transformStyle: 'preserve-3d' }}
                          animate={{ rotateY: isRevealed ? 180 : 0 }}
                          transition={{ duration: 0.8, ease: 'easeInOut' }}
                        >
                          {/* Card Back */}
                          <div
                            className="absolute inset-0 rounded-lg shadow-2xl overflow-hidden border-2 border-amber-600"
                            style={{
                              backfaceVisibility: 'hidden',
                              WebkitBackfaceVisibility: 'hidden',
                            }}
                          >
                            <div className="absolute inset-0 bg-gradient-to-br from-purple-900 via-purple-800 to-purple-900">
                              <div
                                className="absolute inset-0 opacity-10"
                                style={{
                                  backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)',
                                  backgroundSize: '15px 15px',
                                }}
                              />
                              <div className="absolute inset-0 flex items-center justify-center">
                                <div className="text-center">
                                  <div className="text-4xl mb-2">✦</div>
                                  <div className="text-sm text-purple-200 font-heading tracking-widest">
                                    TAROT
                                  </div>
                                  <div className="text-4xl mt-2">✦</div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Card Front */}
                          <div
                            className="absolute inset-0 rounded-lg shadow-2xl overflow-hidden bg-white"
                            style={{
                              backfaceVisibility: 'hidden',
                              WebkitBackfaceVisibility: 'hidden',
                              transform: 'rotateY(180deg)',
                            }}
                          >
                            <img
                              src={`${card.image_path}`}
                              alt={`${card.full_name || card.name}${card.reversed ? ' (reversed)' : ''}`}
                              className="w-full h-full object-cover"
                              style={card.reversed ? { transform: 'rotate(180deg)' } : undefined}
                            />
                            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3">
                              <h3 className="text-white font-heading text-sm text-center font-semibold">
                                {card.full_name || card.name}
                              </h3>
                              {card.reversed && (
                                <p className="text-amber-300 text-[10px] text-center uppercase tracking-widest mt-0.5">Reversed</p>
                              )}
                            </div>
                          </div>
                        </motion.div>

                        {/* Sparkles on reveal */}
                        <AnimatePresence>
                          {isRevealed && revealedCards[revealedCards.length - 1] === card.id && (
                            <>
                              {[...Array(6)].map((_, i) => (
                                <motion.div
                                  key={i}
                                  className="absolute text-yellow-400 pointer-events-none"
                                  style={{
                                    left: '50%',
                                    top: '50%',
                                  }}
                                  initial={{
                                    scale: 0,
                                    x: 0,
                                    y: 0,
                                  }}
                                  animate={{
                                    scale: [0, 1.5, 0],
                                    x: (Math.random() - 0.5) * 100,
                                    y: (Math.random() - 0.5) * 100,
                                    opacity: [0, 1, 0],
                                  }}
                                  transition={{
                                    duration: 1,
                                    ease: 'easeOut',
                                  }}
                                >
                                  ✨
                                </motion.div>
                              ))}
                            </>
                          )}
                        </AnimatePresence>
                      </motion.div>

                      {/* Position Label */}
                      <motion.div
                        className="mt-3 text-center"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: index * 0.2 + 0.3 }}
                      >
                        <p className="text-sm text-amber-200 font-semibold">
                          {positionName(index)}
                        </p>
                      </motion.div>
                    </motion.div>
                  );
                })}
              </motion.div>
            ) : (
              /* Minimized Cards — only shown after gate is unlocked */
              <div className="mb-8">
                {emailGateUnlocked && (
                <motion.div
                  className="flex justify-center items-center gap-2"
                  initial={{ opacity: 0, y: -50 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8 }}
                >
                  {cards.map((card, index) => (
                    <motion.div
                      key={card.id}
                      className="relative cursor-pointer"
                      initial={{ scale: 0, rotate: 360 }}
                      animate={{ scale: 1, rotate: 0 }}
                      transition={{ delay: index * 0.1, type: 'spring', stiffness: 200 }}
                      onClick={() => setSelectedCardModal(selectedCardModal?.card.id === card.id ? null : { card, index })}
                    >
                      <motion.div
                        className="relative rounded shadow-lg overflow-hidden border border-amber-600"
                        style={{ width: '40px', height: '60px' }}
                        whileHover={{ scale: 1.2, zIndex: 10 }}
                        whileTap={{ scale: 0.95 }}
                        animate={{
                          boxShadow: selectedCardModal?.card.id === card.id
                            ? '0 0 30px rgba(251, 191, 36, 1)'
                            : [
                                '0 0 10px rgba(251, 191, 36, 0.5)',
                                '0 0 20px rgba(251, 191, 36, 0.8)',
                                '0 0 10px rgba(251, 191, 36, 0.5)',
                              ],
                        }}
                        transition={{
                          duration: 2,
                          repeat: Infinity,
                          delay: index * 0.2,
                        }}
                      >
                        <img
                          src={`${card.image_path}`}
                          alt={card.full_name || card.name}
                          className="w-full h-full object-cover"
                        />
                      </motion.div>
                    </motion.div>
                  ))}
                </motion.div>
                )}

                {/* Expanded Card Details */}
                <AnimatePresence>
                  {selectedCardModal && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.4 }}
                      className="overflow-hidden"
                    >
                      <motion.div
                        className="mt-6 bg-gradient-to-br from-purple-900/50 to-indigo-900/50 backdrop-blur rounded-2xl overflow-hidden border border-purple-400/30 max-w-3xl mx-auto"
                        initial={{ y: -20 }}
                        animate={{ y: 0 }}
                        exit={{ y: -20 }}
                      >
                        <div className="flex flex-col md:flex-row">
                          {/* Card Image */}
                          <div className="md:w-1/2 p-6 flex items-center justify-center bg-gradient-to-br from-purple-800/30 to-indigo-900/30">
                            <motion.img
                              src={`${selectedCardModal.card.image_path}`}
                              alt={selectedCardModal.card.full_name || selectedCardModal.card.name}
                              className="w-full max-w-xs rounded-xl shadow-2xl border-2 border-amber-400"
                              initial={{ scale: 0.8, rotateY: -90 }}
                              animate={{ scale: 1, rotateY: 0 }}
                              transition={{ duration: 0.6, ease: "easeOut" }}
                            />
                          </div>

                          {/* Card Details */}
                          <div className="md:w-1/2 p-6">
                            <div className="mb-3">
                              <span className="text-amber-300 text-xs font-semibold uppercase tracking-wider">
                                {positionName(selectedCardModal.index)}
                              </span>
                            </div>

                            <h2 className="text-2xl md:text-3xl font-heading font-bold text-white mb-4">
                              {selectedCardModal.card.full_name || selectedCardModal.card.name}
                            </h2>

                            <div className="space-y-4">
                              <div>
                                <h3 className="text-lg font-heading font-semibold text-amber-200 mb-2">Meaning</h3>
                                <p className="text-purple-100 leading-relaxed text-sm">
                                  {selectedCardModal.card.description || selectedCardModal.card.meaning || 'A powerful card in the tarot deck, offering deep insights and guidance for your journey.'}
                                </p>
                              </div>

                              {selectedCardModal.card.keywords && (
                                <div>
                                  <h3 className="text-lg font-heading font-semibold text-amber-200 mb-2">Keywords</h3>
                                  <p className="text-purple-100 text-sm">
                                    {selectedCardModal.card.keywords}
                                  </p>
                                </div>
                              )}
                            </div>

                            {selectedCardModal.card.slug && (
                              <a
                                href={`/cards/${selectedCardModal.card.slug}`}
                                className="mt-6 block w-full text-center bg-gradient-to-r from-amber-500 via-purple-500 to-pink-500 text-white px-4 py-2 rounded-full font-semibold hover:shadow-[0_0_20px_rgba(251,191,36,0.6)] transition-all"
                              >
                                Read full {selectedCardModal.card.full_name || selectedCardModal.card.name} meaning →
                              </a>
                            )}
                            <button
                              onClick={() => setSelectedCardModal(null)}
                              className="mt-3 w-full text-purple-300 hover:text-amber-300 px-4 py-2 text-sm transition-colors"
                            >
                              Close Details
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}
          </AnimatePresence>

          {/* Email Gate */}
          <AnimatePresence>
            {showInterpretation && !emailGateUnlocked && (
              <motion.div
                className="max-w-3xl mx-auto"
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.7, ease: 'easeOut' }}
              >
                {/* Cards preview — bigger, with names + keywords */}
                <motion.div
                  className="flex justify-center items-start gap-4 md:gap-6 flex-wrap mb-10"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                >
                  {cards.map((card, index) => (
                    <motion.div
                      key={card.id}
                      className="flex flex-col items-center gap-2 text-center"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 + index * 0.15 }}
                      style={{ width: 'clamp(90px, 18vw, 130px)' }}
                    >
                      <motion.div
                        className="relative rounded-xl overflow-hidden shadow-2xl border-2 border-amber-500/60 w-full"
                        style={{ aspectRatio: '2/3' }}
                        animate={{ boxShadow: ['0 0 15px rgba(251,191,36,0.3)', '0 0 30px rgba(251,191,36,0.6)', '0 0 15px rgba(251,191,36,0.3)'] }}
                        transition={{ duration: 2.5, repeat: Infinity, delay: index * 0.3 }}
                      >
                        <img
                          src={card.image_path}
                          alt={`${card.full_name || card.name}${card.reversed ? ' (reversed)' : ''}`}
                          className="w-full h-full object-cover"
                          style={card.reversed ? { transform: 'rotate(180deg)' } : undefined}
                        />
                      </motion.div>
                      <div>
                        <p className="text-amber-300 text-xs font-semibold uppercase tracking-wider mb-0.5">
                          {positionName(index)}
                        </p>
                        <p className="text-white font-heading font-bold text-sm leading-tight">
                          {card.full_name || card.name}
                          {card.reversed && <span className="block text-amber-300 text-[10px] uppercase tracking-widest mt-0.5">Reversed</span>}
                        </p>
                        {card.keywords && (
                          <p className="text-purple-300 text-xs mt-1 leading-tight">
                            {card.keywords.split(',').slice(0, 2).join(' · ')}
                          </p>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </motion.div>

                {/* Blurred interpretation preview */}
                <motion.div
                  className="relative mb-8 rounded-2xl overflow-hidden"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4 }}
                >
                  <div className="p-6 bg-purple-900/40 border border-purple-400/20 rounded-2xl select-none" style={{ filter: 'blur(4px)' }}>
                    <p className="text-purple-100 text-sm leading-relaxed">
                      {interpretation
                        ? interpretation.replace(/<[^>]+>/g, '').slice(0, 220)
                        : `The cards you have drawn carry a powerful message for your question. ${cards.map(c => c.full_name || c.name).join(', ')} — together they form a constellation of meaning that speaks directly to your situation. The energies at play reveal both the challenges ahead and the gifts available to you on this path.`
                      }...
                    </p>
                  </div>
                  {/* Gradient fade over the blur */}
                  <div className="absolute inset-0 bg-gradient-to-b from-transparent via-indigo-950/60 to-indigo-950 rounded-2xl" />
                  <div className="absolute inset-0 flex items-end justify-center pb-4">
                    <span className="text-amber-300 text-xs font-semibold tracking-widest uppercase flex items-center gap-2">
                      <span>🔒</span> Full reading locked
                    </span>
                  </div>
                </motion.div>

                {/* Email gate panel */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                >
                  <div className="relative">
                    <motion.div
                      className="absolute -inset-1 bg-gradient-to-r from-amber-500 via-purple-500 to-pink-500 rounded-3xl blur opacity-25"
                      animate={{ opacity: [0.15, 0.35, 0.15] }}
                      transition={{ duration: 3, repeat: Infinity }}
                    />
                    <div className="relative bg-gradient-to-br from-purple-900/95 to-indigo-900/95 backdrop-blur-xl p-8 rounded-3xl border border-purple-400/30 shadow-2xl text-center">
                      <h2 className="text-2xl md:text-3xl font-heading font-bold text-transparent bg-gradient-to-r from-amber-200 to-purple-300 bg-clip-text mb-2">
                        Unlock your full cosmic reading
                      </h2>
                      <p className="text-purple-200 text-sm mb-6 max-w-sm mx-auto">
                        Your personalized AI interpretation — what each card means for <span className="text-amber-300 font-medium">your</span> specific question, and the path forward.
                      </p>

                      <form onSubmit={handleEmailSubmit} className="flex flex-col gap-3 max-w-sm mx-auto">
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => { setEmail(e.target.value); setEmailError(''); }}
                          placeholder="your@email.com"
                          required
                          className="bg-purple-950/70 border border-purple-500/40 rounded-full px-5 py-3 text-white placeholder-purple-400/50 text-sm focus:outline-none focus:border-amber-400 transition-colors"
                        />
                        {emailError && (
                          <p className="text-red-400 text-xs text-center -mt-1">{emailError}</p>
                        )}
                        <motion.button
                          type="submit"
                          disabled={emailLoading}
                          className="bg-gradient-to-r from-amber-500 to-amber-600 text-white px-7 py-3 rounded-full font-bold text-sm shadow-lg disabled:opacity-70 flex items-center justify-center gap-2"
                          whileHover={!emailLoading ? { scale: 1.04, boxShadow: '0 0 28px rgba(245,158,11,0.6)' } : {}}
                          whileTap={!emailLoading ? { scale: 0.96 } : {}}
                        >
                          {emailLoading ? (
                            <>
                              <motion.span
                                className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full"
                                animate={{ rotate: 360 }}
                                transition={{ duration: 0.8, repeat: Infinity, ease: 'linear' }}
                              />
                              Verifying...
                            </>
                          ) : (
                            '✦ Reveal my reading'
                          )}
                        </motion.button>
                      </form>
                      <p className="text-purple-500 text-xs mt-4">We verify your email is real. No spam, ever.</p>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* AI Interpretation */}
          <AnimatePresence>
            {showInterpretation && emailGateUnlocked && (
              <motion.div
                className="max-w-4xl mx-auto"
                initial={{ opacity: 0, y: 50, scale: 0.9 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -50, scale: 0.9 }}
                transition={{ duration: 0.8, type: 'spring', stiffness: 100, damping: 15 }}
              >
                {interpretation ? (
                  <div className="relative">
                    {/* Glowing border effect */}
                    <motion.div
                      className="absolute -inset-1 bg-gradient-to-r from-amber-500 via-purple-500 to-pink-500 rounded-3xl blur opacity-30"
                      animate={{
                        opacity: [0.2, 0.4, 0.2],
                      }}
                      transition={{
                        duration: 3,
                        repeat: Infinity,
                      }}
                    />

                    <div className="relative bg-gradient-to-br from-purple-900/90 to-indigo-900/90 backdrop-blur-xl p-8 md:p-10 rounded-3xl border-2 border-purple-400/30 shadow-2xl">
                      {/* Header */}
                      <motion.div
                        className="text-center mb-6"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.3 }}
                      >
                        <motion.div
                          className="text-6xl mb-3"
                          animate={{
                            rotate: [0, 10, -10, 0],
                          }}
                          transition={{
                            duration: 2,
                            repeat: Infinity,
                            ease: 'easeInOut',
                          }}
                        >
                          ✨
                        </motion.div>
                        <h2 className="text-3xl font-heading font-bold text-transparent bg-gradient-to-r from-amber-200 to-purple-300 bg-clip-text mb-3">
                          Your Cosmic Guidance
                        </h2>
                        <div className="h-1 w-24 bg-gradient-to-r from-amber-500 via-purple-500 to-pink-500 mx-auto rounded-full"></div>
                    </motion.div>

                    {/* Yes/No verdict badge */}
                    {verdictStyle && (
                      <motion.div
                        className="flex justify-center mb-8"
                        initial={{ opacity: 0, scale: 0.6 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.4, type: 'spring', stiffness: 200, damping: 14 }}
                      >
                        <div
                          className={`inline-flex items-center gap-3 px-10 py-4 rounded-2xl bg-gradient-to-r ${verdictStyle.gradient} shadow-2xl`}
                          style={{ boxShadow: `0 0 40px ${verdictStyle.glow}` }}
                        >
                          <span className="text-3xl">{verdictStyle.emoji}</span>
                          <span className="text-4xl md:text-5xl font-heading font-extrabold text-white tracking-wide drop-shadow">
                            {verdictStyle.label}
                          </span>
                        </div>
                      </motion.div>
                    )}

                    {/* Interpretation with typewriter effect */}
                    <motion.div
                      className="prose prose-invert max-w-none"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.6 }}
                    >
                      <div
                        className="text-gray-300 leading-relaxed whitespace-pre-wrap"
                        dangerouslySetInnerHTML={{ __html: interpretation }}
                      />
                    </motion.div>

                    {/* Action Buttons */}
                    <motion.div
                      className="mt-8 flex justify-center gap-4"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 1.0 }}
                    >
                      <motion.a
                        href="/"
                        className="bg-gradient-mystic text-white px-8 py-3 rounded-full font-semibold inline-flex items-center gap-2"
                        whileHover={{ scale: 1.05, boxShadow: '0 0 30px rgba(168, 85, 247, 0.6)' }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <span>✨</span> New Reading
                      </motion.a>
                    </motion.div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-8">
                    {/* Loading message */}
                    <div className="text-center mb-8">
                      <div className="relative inline-block mb-4">
                        <motion.div
                          className="w-16 h-16 rounded-full border-4 border-purple-500/30"
                          animate={{
                            rotate: 360,
                          }}
                          transition={{
                            duration: 3,
                            repeat: Infinity,
                            ease: 'linear',
                          }}
                        />
                        <motion.div
                          className="absolute inset-0 w-16 h-16 rounded-full border-4 border-transparent border-t-amber-400"
                          animate={{
                            rotate: -360,
                          }}
                          transition={{
                            duration: 2,
                            repeat: Infinity,
                            ease: 'linear',
                          }}
                        />
                        <div className="absolute inset-0 flex items-center justify-center text-2xl">
                          🔮
                        </div>
                      </div>
                      <motion.p
                        className="text-purple-100 text-xl font-heading"
                        animate={{
                          opacity: [0.5, 1, 0.5],
                        }}
                        transition={{
                          duration: 2,
                          repeat: Infinity,
                          ease: 'easeInOut',
                        }}
                      >
                        The cosmos is aligning your interpretation...
                      </motion.p>
                      <p className="text-purple-300 text-sm mt-2">
                        Meanwhile, explore your cards below
                      </p>
                    </div>

                    {/* Display cards while waiting */}
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {cards.map((card, index) => (
                        <motion.div
                          key={card.id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.1 }}
                          className="bg-gradient-to-br from-purple-900/50 to-indigo-900/50 backdrop-blur rounded-2xl overflow-hidden border border-purple-400/30"
                        >
                          {/* Card Image */}
                          <div className="relative aspect-[2/3] bg-gradient-to-br from-purple-800 to-indigo-900">
                            <img
                              src={`${card.image_path}`}
                              alt={card.full_name || card.name}
                              className="w-full h-full object-cover"
                            />
                          </div>

                          {/* Card Info */}
                          <div className="p-4">
                            <div className="mb-2">
                              <span className="text-amber-300 text-xs font-semibold uppercase tracking-wider">
                                {positionName(index)}
                              </span>
                            </div>
                            <h3 className="text-xl font-heading font-bold text-white mb-2">
                              {card.slug ? (
                                <a href={`/cards/${card.slug}`} className="hover:text-amber-300 transition-colors">
                                  {card.full_name || card.name}
                                </a>
                              ) : (
                                card.full_name || card.name
                              )}
                            </h3>
                            <p className="text-purple-200 text-sm leading-relaxed line-clamp-4">
                              {card.description || card.meaning || 'A powerful card in the tarot deck, offering deep insights and guidance for your journey.'}
                            </p>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      <Footer />
      </div>
    </>
  );
}